/**
 * Client-side world-model mirror.
 *
 * Mirrors the subset of `mytile-world::WorldState` that the renderer + UI
 * actually read. Hydrated from `SnapshotMessage` (Fresh/Full path) and kept
 * in sync by applying `WorldEvent`s from `WorldEventBatch`es.
 *
 * Determinism guarantee: applying the same event sequence to equal initial
 * state yields equal final state (mirrors SERVER's I1). Tested via
 * snapshot-hash equivalence in `appliers.test.ts`.
 *
 * Storage choices optimised for render-path hot access:
 *   - Dense tile arrays (row-major Uint8Array) for tile material / biome /
 *     elevation lookups — avoids per-tile object allocation for a 512×512
 *     world (~260 k tiles).
 *   - `Map<edgeKeyString, WallEntry>` for walls. Canonical serialised keys
 *     ("h:3,4" / "v:7,2") give stable O(1) lookup without a nested map.
 *   - `Map<PlayerId, PlayerEntry>` for players. Mutation-friendly and
 *     small-cardinality.
 *   - `Uint8Array` fog bitmap, bit-per-tile, row-major — mirrors
 *     `SnapshotMessage.fog.bitmap` without re-packing.
 */

import type {
  CardinalDirection,
  ChatZone,
  DoorState,
  EdgeAxis,
  EdgeKey,
  EntityId,
  PlayerId,
  PropId,
  PropKind,
  Role,
  RoomArchetype,
  RoomId,
  TileCoord,
  WallMaterial,
  WeatherKind,
} from '../wire';

// ─── Helpers ──────────────────────────────────────────────────────────────

/** Canonical serialised key for an `EdgeKey`. Stable across applier calls. */
export function edgeKeyString(edge: EdgeKey): string {
  const sigil = edge.axis === 'Horizontal' ? 'h' : 'v';
  return `${sigil}:${edge.tile[0]},${edge.tile[1]}`;
}

/** Inverse of `edgeKeyString` — primarily for debugging / test assertions. */
export function edgeKeyFromString(s: string): EdgeKey {
  const [sigil, coords] = s.split(':') as [string, string];
  const [x, y] = coords.split(',').map((n) => Number.parseInt(n, 10)) as [
    number,
    number,
  ];
  return {
    tile: [x, y] as TileCoord,
    axis: sigil === 'h' ? ('Horizontal' as EdgeAxis) : ('Vertical' as EdgeAxis),
  };
}

/** Index into a row-major dense array. */
export function tileIndex(x: number, y: number, width: number): number {
  return y * width + x;
}

/** Bit-addressed read of the fog bitmap. `true` = revealed. */
export function fogBitGet(
  bitmap: Uint8Array | number[],
  index: number,
): boolean {
  const byte = index >>> 3;
  const bit = index & 7;
  const b = (bitmap as { [i: number]: number | undefined })[byte];
  return ((b ?? 0) & (1 << bit)) !== 0;
}

/** Bit-addressed write of the fog bitmap. `set=true` reveals the tile. */
export function fogBitSet(
  bitmap: Uint8Array,
  index: number,
  set: boolean,
): void {
  const byte = index >>> 3;
  const bit = index & 7;
  if (set) {
    bitmap[byte] = (bitmap[byte] ?? 0) | (1 << bit);
  } else {
    bitmap[byte] = (bitmap[byte] ?? 0) & ~(1 << bit);
  }
}

// ─── Entity shapes ────────────────────────────────────────────────────────

export interface PlayerEntry {
  id: PlayerId;
  display_name: string;
  position: TileCoord;
  facing: CardinalDirection;
  role: Role;
  connected: boolean;
  /** Last chat utterances, most-recent first, capped per CLIENT_DESIGN §11.6. */
  recent_utterances: string[];
}

export type EdgeOccupant =
  | { kind: 'wall'; material: WallMaterial; placed_by: PlayerId | null }
  | {
      kind: 'door';
      material: WallMaterial;
      state: DoorState;
      placed_by: EntityId | null;
    };

export interface WallEntry {
  edge: EdgeKey;
  occupant: EdgeOccupant;
}

export interface RoomEntry {
  id: RoomId;
  archetype: RoomArchetype;
  tiles: TileCoord[];
}

export interface PropEntry {
  id: PropId;
  kind: PropKind;
  position: TileCoord;
  placer: EntityId | null;
}

/** Time-of-day phase derived from day_tick. M2.1 addition. */
export type TimeOfDayPhase = 'Night' | 'Dawn' | 'Day' | 'Dusk';

/** Precipitation type. M2.1 addition. */
export type PrecipType = 'None' | 'Rain' | 'Snow' | 'Sleet';

export interface ClockState {
  tick: bigint;
  /** Current tick within the day [0, ticks_per_day). M2.1 addition. */
  day_tick: number;
  day: number;
  season: 'Spring' | 'Summer' | 'Autumn' | 'Winter';
  /** Day within the current season [0, days_per_season). M2.1 addition. */
  season_day: number;
  /** Year count since world creation (0-indexed). M2.1 addition. */
  year: number;
  /** Current time-of-day phase. M2.1 addition. */
  phase: TimeOfDayPhase;
}

export interface WeatherState {
  kind: WeatherKind;
  intensity: number;
  target_at: bigint;
  /** Temperature in degrees C. M2.1 addition. */
  temperature: number;
  /** Apparent temperature in degrees C (wind chill / heat index). */
  apparent_temperature: number;
  /** Relative humidity [0, 1]. */
  humidity: number;
  /** Atmospheric pressure, normalized [0, 1]. */
  pressure: number;
  /** Wind bearing in radians [0, 2pi). */
  wind_direction: number;
  /** Wind speed, normalized [0, 1]. */
  wind_speed: number;
  /** Cloud cover [0, 1]. */
  cloud_cover: number;
  /** Precipitation intensity [0, 1]. */
  precip_intensity: number;
  /** Precipitation type. */
  precip_type: PrecipType;
  /** Whether thunderstorm conditions are active. */
  thunderstorm_active: boolean;
  /** Fog density [0, 1]. */
  fog_density: number;
  /** Visibility [0, 1]. 1 = full. */
  visibility: number;
}

/** Speech-bubble payload spawned by a `Spoke` event. Renderer TTLs these. */
export interface SpeechBubble {
  /** Client-local monotonic id; the renderer keys its per-bubble timer
   *  state (fade-start wall-clock timestamp) on this. */
  id: number;
  speaker: EntityId;
  utterance: string;
  zone: ChatZone;
  position: TileCoord;
  /** Tick at which this utterance was emitted. */
  tick: bigint;
}

// ─── Model ────────────────────────────────────────────────────────────────

export interface WorldModel {
  width: number;
  height: number;
  /** Current server tick last applied. `0n` before any snapshot. */
  tick: bigint;

  // Terrain
  tileMaterials: Uint8Array;
  tileBiomes: Uint8Array;
  tileElevations: Uint8Array;
  /** Int32 because RoomId is `u32` on the wire but we use `-1` for "no room". */
  tileRoomIds: Int32Array;

  // Structure
  walls: Map<string, WallEntry>;
  rooms: Map<number, RoomEntry>;
  props: Map<number, PropEntry>;

  // Entities
  players: Map<number, PlayerEntry>;

  // Ambient
  clock: ClockState;
  weather: WeatherState;

  // Per-player data (viewer only)
  fog: Uint8Array;

  // Ephemeral UI state driven by world events — renderer reads and decays.
  speech_bubbles: SpeechBubble[];
}

/** Create an empty (not yet hydrated) model. Zero-sized arrays so consumers
 *  can rely on non-null buffer refs before `applySnapshot()` runs. */
export function createEmptyWorldModel(): WorldModel {
  return {
    width: 0,
    height: 0,
    tick: 0n,
    tileMaterials: new Uint8Array(0),
    tileBiomes: new Uint8Array(0),
    tileElevations: new Uint8Array(0),
    tileRoomIds: new Int32Array(0),
    walls: new Map(),
    rooms: new Map(),
    props: new Map(),
    players: new Map(),
    clock: { tick: 0n, day_tick: 400, day: 0, season: 'Spring', season_day: 0, year: 0, phase: 'Day' },
    weather: {
      kind: 'Clear', intensity: 0, target_at: 0n,
      temperature: 20, apparent_temperature: 20, humidity: 0.5, pressure: 0.5,
      wind_direction: 0, wind_speed: 0, cloud_cover: 0, precip_intensity: 0,
      precip_type: 'None', thunderstorm_active: false, fog_density: 0, visibility: 1,
    },
    fog: new Uint8Array(0),
    speech_bubbles: [],
  };
}
